// ─────────────────────────────────────────────────────────────────
// Email attachments
// ----------------------------------------------------------------
// Attachments are stored on the email row and validated by
// EmailAttachmentSchema (packages/shared/src/emails.ts). The checks
// below mirror that schema so mistakes surface in the panel instead
// of coming back as a 400 from PUT /api/emails/:id.
// ─────────────────────────────────────────────────────────────────
import { EmailAttachmentSchema, type EmailAttachment } from '@blogforge/shared';
import { Paperclip, Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../hooks/api';

/** Reads the attachment list off the email row, keeping anything that looks like one. */
function parseAttachments(raw: unknown): EmailAttachment[] {
  if (!Array.isArray(raw)) return [];
  const attachments: EmailAttachment[] = [];
  for (const value of raw) {
    if (!value || typeof value !== 'object') continue;
    const source = value as Record<string, unknown>;
    attachments.push({
      filename: typeof source['filename'] === 'string' ? source['filename'] : '',
      contentType: typeof source['contentType'] === 'string' ? source['contentType'] : '',
      href: typeof source['href'] === 'string' ? source['href'] : '',
    });
  }
  return attachments;
}

/**
 * Content snapshot of one attachment, independent of key order — Postgres hands
 * jsonb back with its own ordering, so comparing the raw JSON would report a
 * change after every save. Unlike the variables record this is a list, and the
 * order is meaningful (parts are appended in it), so it is preserved.
 */
function attachmentSnapshot(attachment: EmailAttachment): unknown[] {
  return [attachment.filename, attachment.contentType, attachment.href];
}

function attachmentsSnapshot(attachments: EmailAttachment[]): string {
  return JSON.stringify(attachments.map(attachmentSnapshot));
}

/** Client-side mirror of EmailAttachmentSchema. */
function attachmentProblem(attachment: EmailAttachment): string | null {
  const filename = attachment.filename.trim();
  if (filename === '') return 'Give the attachment a filename.';
  if (attachment.contentType.trim() === '') {
    return `Give "${filename}" a content type, such as application/pdf.`;
  }
  if (attachment.href.trim() === '') return `Give "${filename}" a link.`;
  // The only remaining constraint in the schema is a parseable absolute URL.
  if (!EmailAttachmentSchema.safeParse(attachment).success) {
    return `The link for "${filename}" must be a full URL, such as https://example.com/${filename}.`;
  }
  return null;
}

/** Duplicated names collide as MIME parts in the recipient's client. */
function duplicateAttachmentProblem(attachments: EmailAttachment[]): string | null {
  const seen = new Set<string>();
  for (const attachment of attachments) {
    const filename = attachment.filename.trim();
    if (filename === '') continue;
    const key = filename.toLowerCase();
    if (seen.has(key)) {
      return `Two attachments are named "${filename}" — filenames must be unique.`;
    }
    seen.add(key);
  }
  return null;
}

function attachmentsProblem(attachments: EmailAttachment[]): string | null {
  for (const attachment of attachments) {
    const problem = attachmentProblem(attachment);
    if (problem) return problem;
  }
  return duplicateAttachmentProblem(attachments);
}

/** The email route answers with a zod issue array — surface the first issue. */
function attachmentsErrorMessage(cause: unknown): string {
  if (cause instanceof ApiError) {
    const detail: unknown = cause.body?.['error'];
    if (Array.isArray(detail) && detail.length > 0) {
      const [first] = detail as unknown[];
      if (first && typeof first === 'object') {
        const issue = first as { message?: unknown; path?: unknown };
        const path =
          Array.isArray(issue.path) && issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
        return `${path}${typeof issue.message === 'string' ? issue.message : 'Invalid attachment'}`;
      }
    }
    return cause.message;
  }
  return cause instanceof Error ? cause.message : 'Could not save attachments.';
}

interface NewAttachmentForm {
  filename: string;
  contentType: string;
  href: string;
}

const EMPTY_ATTACHMENT_FORM: NewAttachmentForm = {
  filename: '',
  contentType: '',
  href: '',
};

interface EmailAttachmentEditorProps {
  /** Attachments currently stored on the email (server truth). */
  rawAttachments: unknown;
  /** Persists the complete list — the endpoint replaces it wholesale. */
  onSave: (next: EmailAttachment[]) => Promise<unknown>;
}

/**
 * Lists the files attached to the email. Every edit is written through in one
 * PUT of the whole list, so `working` is a local mirror: it keeps rapid
 * consecutive edits from clobbering each other while a request is still in
 * flight, and adopts the server value once it lands.
 */
export function EmailAttachmentEditor({ rawAttachments, onSave }: EmailAttachmentEditorProps) {
  const attachments = parseAttachments(rawAttachments);

  const [working, setWorking] = useState<EmailAttachment[]>(attachments);
  const [syncedFrom, setSyncedFrom] = useState(() => attachmentsSnapshot(attachments));
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<NewAttachmentForm>(EMPTY_ATTACHMENT_FORM);
  /** Bumped whenever stored attachments replace the local ones, to re-seed inputs. */
  const [generation, setGeneration] = useState(0);

  // Adopt the stored value whenever the query hands back a different one, but
  // leave an echo of our own save (and anything still queued locally) alone.
  const stored = attachmentsSnapshot(attachments);
  if (syncedFrom !== stored) {
    setSyncedFrom(stored);
    if (stored !== attachmentsSnapshot(working)) {
      setWorking(attachments);
      setStatus('idle');
      setError(null);
      setGeneration((prev) => prev + 1);
    }
  }

  const busy = status === 'saving';

  const apply = (next: EmailAttachment[]) => {
    setWorking(next);
    const problem = attachmentsProblem(next);
    if (problem) {
      // Keep the edit on screen but out of the API — the record must stay valid.
      setStatus('error');
      setError(problem);
      return;
    }
    setStatus('saving');
    setError(null);
    void onSave(next).then(
      () => setStatus('saved'),
      (cause: unknown) => {
        setStatus('error');
        setError(attachmentsErrorMessage(cause));
      },
    );
  };

  const patch = (index: number, changes: Partial<EmailAttachment>) =>
    apply(
      working.map((attachment, at) => (at === index ? { ...attachment, ...changes } : attachment)),
    );

  /** Commits an edited field, skipping the round trip when nothing changed. */
  const commit = (index: number, changes: Partial<EmailAttachment>) => {
    const attachment = working[index];
    if (!attachment) return;
    const changed = (Object.keys(changes) as (keyof EmailAttachment)[]).some(
      (key) => attachment[key] !== changes[key],
    );
    if (!changed) return;
    patch(index, changes);
  };

  const remove = (index: number) => {
    const attachment = working[index];
    if (!attachment) return;
    const label = attachment.filename.trim() || 'without a filename';
    if (!confirm(`Remove the attachment "${label}"? It will be dropped from this email.`)) {
      return;
    }
    apply(working.filter((_, at) => at !== index));
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const attachment: EmailAttachment = {
      filename: form.filename.trim(),
      contentType: form.contentType.trim(),
      href: form.href.trim(),
    };
    const problem =
      attachmentProblem(attachment) ??
      (duplicateAttachmentProblem([...working, attachment])
        ? `An attachment named "${attachment.filename}" is already in the list.`
        : null);
    if (problem) {
      setStatus('error');
      setError(problem);
      return;
    }
    setForm(EMPTY_ATTACHMENT_FORM);
    setAdding(false);
    apply([...working, attachment]);
  };

  const cancelAdd = () => {
    setForm(EMPTY_ATTACHMENT_FORM);
    setAdding(false);
    setStatus('idle');
    setError(null);
  };

  return (
    <div className="mt-2 space-y-2">
      {working.length === 0 && !adding && (
        <p className="text-muted text-xs">
          No attachments yet — add one to reference a file from this email.
        </p>
      )}

      {working.length > 0 && (
        <ul className="space-y-2">
          {working.map((attachment, index) => (
            <li
              key={`${index}:${generation}`}
              className="border-line bg-card space-y-2 rounded-lg border p-2.5"
            >
              <div className="flex items-center gap-1.5">
                <Paperclip size={13} className="text-muted shrink-0" />
                <code className="text-ink min-w-0 flex-1 truncate font-mono text-xs">
                  {attachment.filename.trim() || '(no filename)'}
                </code>
                <button
                  type="button"
                  title={`Remove ${attachment.filename.trim() || 'attachment'}`}
                  aria-label={`Remove ${attachment.filename.trim() || 'attachment'}`}
                  className="text-muted hover:text-danger flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors"
                  onClick={() => remove(index)}
                >
                  <Trash2 size={13} />
                </button>
              </div>

              <input
                className="field-input px-2 py-1 text-base md:text-xs"
                placeholder="Filename, e.g. price-list.pdf"
                aria-label={`Filename of attachment ${index + 1}`}
                defaultValue={attachment.filename}
                disabled={busy}
                onBlur={(e) => commit(index, { filename: e.target.value.trim() })}
              />

              <input
                className="field-input px-2 py-1 text-base md:text-xs"
                placeholder="Content type, e.g. application/pdf"
                aria-label={`Content type of attachment ${index + 1}`}
                defaultValue={attachment.contentType}
                disabled={busy}
                onBlur={(e) => commit(index, { contentType: e.target.value.trim() })}
              />

              <input
                className="field-input px-2 py-1 text-base md:text-xs"
                placeholder="Link, e.g. https://example.com/price-list.pdf"
                aria-label={`Link of attachment ${index + 1}`}
                defaultValue={attachment.href}
                disabled={busy}
                onBlur={(e) => commit(index, { href: e.target.value.trim() })}
              />
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <form onSubmit={submit} className="border-line bg-card-2 space-y-2 rounded-lg border p-2.5">
          <input
            autoFocus
            className="field-input px-2 py-1 font-mono text-xs"
            placeholder="filename.pdf"
            aria-label="New attachment filename"
            value={form.filename}
            onChange={(e) => setForm((prev) => ({ ...prev, filename: e.target.value }))}
          />
          <input
            className="field-input px-2 py-1 text-xs"
            placeholder="Content type, e.g. application/pdf"
            aria-label="New attachment content type"
            value={form.contentType}
            onChange={(e) => setForm((prev) => ({ ...prev, contentType: e.target.value }))}
          />
          <input
            className="field-input px-2 py-1 text-xs"
            placeholder="https://example.com/file.pdf"
            aria-label="New attachment link"
            value={form.href}
            onChange={(e) => setForm((prev) => ({ ...prev, href: e.target.value }))}
          />
          <div className="flex items-center gap-2">
            <button type="submit" className="btn-primary px-3 py-1 text-xs">
              Add attachment
            </button>
            <button type="button" className="btn-ghost px-3 py-1 text-xs" onClick={cancelAdd}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          className="btn-secondary w-full py-1.5 text-xs"
          onClick={() => setAdding(true)}
        >
          <Plus size={13} strokeWidth={2.6} />
          Add attachment
        </button>
      )}

      {status !== 'idle' && (
        <p
          className={`text-xs ${
            status === 'error' ? 'text-danger' : status === 'saved' ? 'text-ok' : 'text-muted'
          }`}
        >
          {status === 'saving'
            ? 'Saving…'
            : (error ?? (status === 'saved' ? 'Attachments saved.' : 'Not saved.'))}
        </p>
      )}
    </div>
  );
}
